import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { parseApiError } from "../utils/errorUtils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { LoadingCard } from "../components/LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/use-toast";
import { formatDatePST } from "../utils/dateUtils";
import PhoneInput from "../components/ui/PhoneInput";
import { formatPhone } from "../utils/phoneUtils";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  Save,
  Users,
  Eye,
  Edit,
  Trash2,
  UserPlus,
  Bell,
  Database,
  AlertTriangle,
  Calendar,
  FolderOpen,
  X,
  Check,
  PackagePlus,
  DollarSign,
  Upload,
  Download,
  ArchiveRestore,
  Palette,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { userService } from "../services/userService";
import { databaseService } from "../services/databaseService";
import { projectService } from "../services/projectService";
import { workItemService } from "../services/workItemService";
import { productionStageService } from "../services/productionStageService";
import { crewService } from "../services/crewService";
import { projectStatusService } from "../services/projectStatusService";
import { companySettingsService } from "../services/companySettingsService";
import ApiKeysTab from "../components/settings/ApiKeysTab";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ProjectDetailDialog from "../components/projects/ProjectDetailDialog";

export default function Settings() {
  const { user, updateUser, changePassword, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Handle tab from URL query parameter
  const initialTab = searchParams.get('tab') || 'profile';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Update URL when tab changes
  const handleTabChange = (value) => {
    setActiveTab(value);
    if (value === 'profile') {
      setSearchParams({});
    } else {
      setSearchParams({ tab: value });
    }
  };
  
  // Profile settings state
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: ""
  });

  // Company settings state  
  const [companyData, setCompanyData] = useState({
    name: "",
    address: "",
    phone: "",
    website: "",
    email: "",
    license: ""
  });

  // Preferences state
  const [preferences, setPreferences] = useState({
    theme: "light",
    timezone: "America/New_York",
    dateFormat: "MM/DD/YYYY",
    currency: "USD"
  });

  // Notifications state
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    weeklyReports: true
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  // User management state
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "user",
    password: ""
  });

  // Security tab state
  const [isSessionsDialogOpen, setIsSessionsDialogOpen] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Project Status Management state
  const [statusMgmtPage, setStatusMgmtPage] = useState(1);
  const [statusMgmtLimit, setStatusMgmtLimit] = useState(10);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [statusMgmtSortBy, setStatusMgmtSortBy] = useState("status_changed_at");
  const [statusMgmtSortOrder, setStatusMgmtSortOrder] = useState("desc");
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [isProjectDetailOpen, setIsProjectDetailOpen] = useState(false);

  // Fetch full project details when a project is selected for viewing
  const { data: selectedProjectData, isLoading: loadingProjectDetails } = useQuery({
    queryKey: ['project-detail', selectedProjectId],
    queryFn: async () => {
      const response = await projectService.getById(selectedProjectId);
      return response.data;
    },
    enabled: !!selectedProjectId && isProjectDetailOpen
  });

  // Project status sort options
  const statusMgmtSortOptions = [
    { value: "name", label: "Project Name" },
    { value: "status", label: "Status" },
    { value: "contract_sign_date", label: "Contract Signed" },
    { value: "start_date", label: "Start Date" },
    { value: "end_date", label: "End Date" },
    { value: "status_changed_at", label: "Status Changed" },
  ];

  // Project status definitions from API
  const [showAddStatusDialog, setShowAddStatusDialog] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [showArchivedStatuses, setShowArchivedStatuses] = useState(false);
  const [statusFormData, setStatusFormData] = useState({
    label: "",
    value: "",
    color: "bg-blue-500",
    is_active: true,
    sort_order: 0,
  });

  const statusColorOptions = [
    { value: "bg-blue-500", label: "Blue" },
    { value: "bg-purple-500", label: "Purple" },
    { value: "bg-cyan-500", label: "Cyan" },
    { value: "bg-yellow-500", label: "Yellow" },
    { value: "bg-orange-500", label: "Orange" },
    { value: "bg-emerald-500", label: "Emerald" },
    { value: "bg-red-500", label: "Red" },
    { value: "bg-indigo-500", label: "Indigo" },
    { value: "bg-teal-500", label: "Teal" },
    { value: "bg-green-600", label: "Green" },
    { value: "bg-gray-500", label: "Gray" },
    { value: "bg-slate-500", label: "Slate" },
    { value: "bg-pink-500", label: "Pink" },
    { value: "bg-rose-500", label: "Rose" },
    { value: "bg-amber-500", label: "Amber" },
    { value: "bg-lime-500", label: "Lime" },
  ];

  // Fetch project status definitions
  const { data: statusDefinitionsData, isLoading: statusDefsLoading } = useQuery({
    queryKey: ['project-status-definitions'],
    queryFn: () => projectStatusService.getAll(),
  });
  const allStatusDefs = statusDefinitionsData?.data || [];
  const activeStatusDefs = allStatusDefs.filter(s => s.is_active !== false);
  const archivedStatusDefs = allStatusDefs.filter(s => s.is_active === false);

  // Build projectStatusOptions from API data for the project table dropdown
  const projectStatusOptions = allStatusDefs.map(s => ({
    value: s.value,
    label: s.label,
  }));

  // Work Items state
  const [workItemSearchTerm, setWorkItemSearchTerm] = useState("");
  const [showWorkItemDialog, setShowWorkItemDialog] = useState(false);
  const [editingWorkItem, setEditingWorkItem] = useState(null);
  const [showArchivedWorkItems, setShowArchivedWorkItems] = useState(false);
  const [workItemFormData, setWorkItemFormData] = useState({
    name: "",
    customer_view: "",
    category: "",
    unit: "ea.",
    price: 0,
    commission: 0,
    labor: 0,
    active: true
  });

  // Production Stages state
  const [showStageDialog, setShowStageDialog] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [stageFormData, setStageFormData] = useState({
    value: "",
    label: "",
    color: "#64748b",
    description: "",
    sort_order: 0,
    is_active: true,
  });

  // Crews state
  const [showCrewDialog, setShowCrewDialog] = useState(false);
  const [editingCrew, setEditingCrew] = useState(null);
  const [crewFormData, setCrewFormData] = useState({
    name: "",
    color: "#2563eb",
    members: [],
    notes: "",
    is_active: true,
    is_hourly: true,
  });

  // Loading states
  const [saving, setSaving] = useState({});

  // API hooks - Users
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAll(),
    enabled: isAdmin()
  });

  // API hooks - Project Status Management
  const { data: statusMgmtData, isLoading: statusMgmtLoading } = useQuery({
    queryKey: ['project-status-management', statusMgmtPage, statusMgmtLimit, statusMgmtSortBy, statusMgmtSortOrder],
    queryFn: () => projectService.getStatusManagement({ 
      page: statusMgmtPage, 
      limit: statusMgmtLimit,
      sort_by: statusMgmtSortBy,
      sort_order: statusMgmtSortOrder
    }),
    enabled: isAdmin()
  });

  // API hooks - Work Items
  const { data: workItemsData, isLoading: workItemsLoading } = useQuery({
    queryKey: ['work-items', showArchivedWorkItems],
    queryFn: () => workItemService.getAll({ show_archived: showArchivedWorkItems }),
    enabled: isAdmin()
  });

  const createWorkItemMutation = useMutation({
    mutationFn: (data) => workItemService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-items'] });
      toast({ description: "Work item created successfully!" });
      setShowWorkItemDialog(false);
      setWorkItemFormData({ name: "", customer_view: "", category: "", unit: "ea.", price: 0, commission: 0, labor: 0, active: true });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to create work item") });
    }
  });

  const updateWorkItemMutation = useMutation({
    mutationFn: ({ id, data }) => workItemService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-items'] });
      toast({ description: "Work item updated successfully!" });
      setShowWorkItemDialog(false);
      setEditingWorkItem(null);
      setWorkItemFormData({ name: "", customer_view: "", category: "", unit: "ea.", price: 0, commission: 0, labor: 0, active: true });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to update work item") });
    }
  });

  const archiveWorkItemMutation = useMutation({
    mutationFn: (id) => workItemService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-items'] });
      toast({ description: "Work item archived successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to archive work item") });
    }
  });

  const restoreWorkItemMutation = useMutation({
    mutationFn: (id) => workItemService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-items'] });
      toast({ description: "Work item restored successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to restore work item") });
    }
  });

  // Production Stages queries/mutations
  const { data: productionStagesData, isLoading: productionStagesLoading } = useQuery({
    queryKey: ['production-stages'],
    queryFn: () => productionStageService.getAll(),
    enabled: isAdmin(),
  });

  const createStageMutation = useMutation({
    mutationFn: (data) => productionStageService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-stages'] });
      toast({ description: "Stage created successfully!" });
      setShowStageDialog(false);
      setEditingStage(null);
      setStageFormData({ value: "", label: "", color: "#64748b", description: "", sort_order: 0, is_active: true });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to create stage") });
    }
  });

  const updateStageMutation = useMutation({
    mutationFn: ({ id, data }) => productionStageService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-stages'] });
      toast({ description: "Stage updated successfully!" });
      setShowStageDialog(false);
      setEditingStage(null);
      setStageFormData({ value: "", label: "", color: "#64748b", description: "", sort_order: 0, is_active: true });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to update stage") });
    }
  });

  const deleteStageMutation = useMutation({
    mutationFn: (id) => productionStageService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-stages'] });
      toast({ description: "Stage deleted successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to delete stage") });
    }
  });

  // Shared company settings query
  const { data: companySettingsData } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => companySettingsService.get(),
  });

  // Populate companyData from shared settings
  useEffect(() => {
    if (companySettingsData?.data) {
      const cs = companySettingsData.data;
      setCompanyData({
        name: cs.name || "",
        address: cs.address || "",
        phone: cs.phone || "",
        website: cs.website || "",
        email: cs.email || "",
        license: cs.license || "",
      });
    }
  }, [companySettingsData]);

  // Crews queries/mutations
  const { data: crewsData, isLoading: crewsLoading } = useQuery({
    queryKey: ['crews'],
    queryFn: () => crewService.getAll(),
    enabled: isAdmin(),
  });

  const createCrewMutation = useMutation({
    mutationFn: (data) => crewService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crews'] });
      toast({ description: "Crew created successfully!" });
      setShowCrewDialog(false);
      setEditingCrew(null);
      setCrewFormData({ name: "", color: "#2563eb", members: [], notes: "", is_active: true, is_hourly: true });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to create crew") });
    }
  });

  const updateCrewMutation = useMutation({
    mutationFn: ({ id, data }) => crewService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crews'] });
      toast({ description: "Crew updated successfully!" });
      setShowCrewDialog(false);
      setEditingCrew(null);
      setCrewFormData({ name: "", color: "#2563eb", members: [], notes: "", is_active: true, is_hourly: true });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to update crew") });
    }
  });

  const deleteCrewMutation = useMutation({
    mutationFn: (id) => crewService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crews'] });
      toast({ description: "Crew deleted successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to delete crew") });
    }
  });

  // Project Status Definition mutations
  const createStatusDefMutation = useMutation({
    mutationFn: (data) => projectStatusService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-status-definitions'] });
      queryClient.invalidateQueries({ queryKey: ['project-statuses'] });
      toast({ description: "Status created successfully!" });
      setShowAddStatusDialog(false);
      setStatusFormData({ label: "", value: "", color: "bg-blue-500", is_active: true, sort_order: 0 });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to create status") });
    }
  });

  const updateStatusDefMutation = useMutation({
    mutationFn: ({ id, data }) => projectStatusService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-status-definitions'] });
      queryClient.invalidateQueries({ queryKey: ['project-statuses'] });
      toast({ description: "Status updated successfully!" });
      setEditingStatus(null);
      setShowAddStatusDialog(false);
      setStatusFormData({ label: "", value: "", color: "bg-blue-500", is_active: true, sort_order: 0 });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to update status") });
    }
  });

  const deleteStatusDefMutation = useMutation({
    mutationFn: (id) => projectStatusService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-status-definitions'] });
      queryClient.invalidateQueries({ queryKey: ['project-statuses'] });
      toast({ description: "Status deleted successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to delete status") });
    }
  });

  const archiveStatusDefMutation = useMutation({
    mutationFn: (id) => projectStatusService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-status-definitions'] });
      queryClient.invalidateQueries({ queryKey: ['project-statuses'] });
      toast({ description: "Status archived successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to archive status") });
    }
  });

  const restoreStatusDefMutation = useMutation({
    mutationFn: (id) => projectStatusService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-status-definitions'] });
      queryClient.invalidateQueries({ queryKey: ['project-statuses'] });
      toast({ description: "Status restored successfully!" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: parseApiError(error, "Failed to restore status") });
    }
  });

  const updateStatusDateMutation = useMutation({
    mutationFn: ({ projectId, updates }) => projectService.inlineUpdate(projectId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-status-management'] });
      toast({ description: "Project updated successfully!" });
      setEditingProjectId(null);
      setEditingValues({});
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to update project")
      });
    }
  });

  const createUserMutation = useMutation({
    mutationFn: (data) => userService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ description: "User created successfully!" });
      setIsUserDialogOpen(false);
      setNewUser({ name: "", email: "", role: "user", password: "" });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to create user")
      });
    }
  });

  const updateUserMutationRQ = useMutation({
    mutationFn: ({ id, data }) => userService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ description: "User updated successfully!" });
      setIsEditUserDialogOpen(false);
      setSelectedUser(null);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to update user")
      });
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId) => userService.delete(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ description: "User deleted successfully!" });
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to delete user")
      });
    }
  });

  const clearDatabaseMutation = useMutation({
    mutationFn: () => databaseService.clearData(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });
      toast({ description: "Database cleared successfully!" });
      setIsDatabaseClearDialogOpen(false);
      setConfirmationText("");
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to clear database")
      });
    }
  });

  const deleteEmailTemplatesMutation = useMutation({
    mutationFn: () => databaseService.deleteEmailTemplates(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });
      toast({ description: `${data.data.deleted_count} email templates deleted successfully!` });
      setIsDeleteEmailTemplatesDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to delete email templates")
      });
    }
  });

  const deleteWorkItemsMutation = useMutation({
    mutationFn: () => databaseService.deleteWorkItems(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });
      toast({ description: `${data.data.deleted_count} work items deleted successfully!` });
      setIsDeleteWorkItemsDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to delete work items")
      });
    }
  });

  const deleteProjectStatusesMutation = useMutation({
    mutationFn: () => databaseService.deleteProjectStatuses(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });
      toast({ description: `${data.data.deleted_count} project statuses deleted successfully!` });
      setIsDeleteProjectStatusesDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to delete project statuses")
      });
    }
  });

  const deleteVendorsMutation = useMutation({
    mutationFn: () => databaseService.deleteVendors(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });
      toast({ description: `${data.data.deleted_count} vendors deleted successfully!` });
      setIsDeleteVendorsDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to delete vendors")
      });
    }
  });
  
  // Proposal Tool sync state
  const [proposalSyncResult, setProposalSyncResult] = useState(null); // dry-run result
  const [proposalSyncing, setProposalSyncing] = useState(false);
  const [proposalSentResult, setProposalSentResult] = useState(null); // real-run result

  const handleProposalDryRun = async () => {
    setProposalSyncing(true);
    setProposalSentResult(null);
    try {
      const resp = await databaseService.backfillProposalTool(true);
      setProposalSyncResult(resp?.data || resp);
    } catch (e) {
      toast({ variant: "destructive", description: parseApiError(e, "Dry run failed") });
    } finally {
      setProposalSyncing(false);
    }
  };

  const handleProposalSync = async () => {
    setProposalSyncing(true);
    try {
      const resp = await databaseService.backfillProposalTool(false);
      const data = resp?.data || resp;
      setProposalSentResult(data);
      setProposalSyncResult(null);
      toast({ description: `Sent ${data.sent} customers to Proposal Tool (${data.failed_count || 0} failed)` });
    } catch (e) {
      toast({ variant: "destructive", description: parseApiError(e, "Sync failed") });
    } finally {
      setProposalSyncing(false);
    }
  };

  // Database admin state
  const [isDatabaseClearDialogOpen, setIsDatabaseClearDialogOpen] = useState(false);
  const [isDeleteEmailTemplatesDialogOpen, setIsDeleteEmailTemplatesDialogOpen] = useState(false);
  const [isDeleteWorkItemsDialogOpen, setIsDeleteWorkItemsDialogOpen] = useState(false);
  const [isDeleteProjectStatusesDialogOpen, setIsDeleteProjectStatusesDialogOpen] = useState(false);
  const [isDeleteVendorsDialogOpen, setIsDeleteVendorsDialogOpen] = useState(false);
  const [collectionsStatus, setCollectionsStatus] = useState(null);
  const [confirmationText, setConfirmationText] = useState("");
  
  const { data: collectionsData, isLoading: collectionsLoading } = useQuery({
    queryKey: ['collections'],
    queryFn: () => databaseService.getCollectionsStatus(),
    enabled: isAdmin()
  });

  const users = usersData?.data || [];

  // Initialize form data from current user
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || ""
      });
      
      // Company data is loaded separately from shared collection

      setPreferences({
        theme: user.preferences?.theme || "light",
        timezone: user.preferences?.timezone || "America/New_York",
        dateFormat: "MM/DD/YYYY",
        currency: "USD"
      });

      setNotifications({
        emailNotifications: user.preferences?.notifications?.email ?? true,
        pushNotifications: user.preferences?.notifications?.push ?? true,
        smsNotifications: user.preferences?.notifications?.sms ?? false,
        weeklyReports: true
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    if (!user) return;
    
    setSaving(prev => ({ ...prev, profile: true }));
    try {
      await updateUser(user.id, profileData);
      console.log("Profile updated successfully");
    } catch (error) {
      console.error("Failed to update profile:", error);
    } finally {
      setSaving(prev => ({ ...prev, profile: false }));
    }
  };

  const handleSaveCompany = async () => {
    setSaving(prev => ({ ...prev, company: true }));
    try {
      await companySettingsService.update(companyData);
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
      toast({ description: "Company settings saved successfully!" });
    } catch (error) {
      console.error("Failed to update company info:", error);
      toast({ variant: "destructive", description: "Failed to save company settings" });
    } finally {
      setSaving(prev => ({ ...prev, company: false }));
    }
  };

  const handleSavePreferences = async () => {
    if (!user) return;
    
    setSaving(prev => ({ ...prev, preferences: true }));
    try {
      await updateUser(user.id, {
        preferences: {
          ...user.preferences,
          theme: preferences.theme,
          timezone: preferences.timezone
        }
      });
      console.log("Preferences updated successfully");
    } catch (error) {
      console.error("Failed to update preferences:", error);
    } finally {
      setSaving(prev => ({ ...prev, preferences: false }));
    }
  };

  const handleSaveNotifications = async () => {
    if (!user) return;
    
    setSaving(prev => ({ ...prev, notifications: true }));
    try {
      await updateUser(user.id, {
        preferences: {
          ...user.preferences,
          notifications: {
            email: notifications.emailNotifications,
            push: notifications.pushNotifications,
            sms: notifications.smsNotifications
          }
        }
      });
      console.log("Notifications updated successfully");
    } catch (error) {
      console.error("Failed to update notifications:", error);
    } finally {
      setSaving(prev => ({ ...prev, notifications: false }));
    }
  };

  const handleChangePassword = async () => {
    setPasswordError("");
    setPasswordSuccess("");

    if (!user || !passwordData.currentPassword || !passwordData.newPassword) {
      setPasswordError("Please fill in all password fields");
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("New passwords don't match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters long");
      return;
    }
    
    setSaving(prev => ({ ...prev, password: true }));
    try {
      await changePassword(user.id, passwordData.currentPassword, passwordData.newPassword);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      setPasswordSuccess("Password changed successfully!");
    } catch (error) {
      console.error("Failed to change password:", error);
      let errorMessage = "Unknown error occurred";
      
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.detail) {
        errorMessage = error.detail;
      }
      
      setPasswordError("Failed to change password: " + errorMessage);
    } finally {
      setSaving(prev => ({ ...prev, password: false }));
    }
  };

  // Mock session data for demonstration
  const mockSessions = [
    {
      id: 1,
      device: "Chrome on Windows",
      location: "San Francisco, CA",
      ip: "192.168.1.1",
      lastActive: "2 minutes ago",
      isCurrent: true
    },
    {
      id: 2,
      device: "Safari on iPhone",
      location: "San Francisco, CA", 
      ip: "192.168.1.2",
      lastActive: "1 hour ago",
      isCurrent: false
    },
    {
      id: 3,
      device: "Firefox on Mac",
      location: "Los Angeles, CA",
      ip: "10.0.0.1",
      lastActive: "2 days ago",
      isCurrent: false
    }
  ];

  const handleViewSessions = () => {
    setIsSessionsDialogOpen(true);
  };

  const handleRevokeSession = (sessionId) => {
    console.log("Revoking session:", sessionId);
    // In real implementation, this would call an API to revoke the session
  };

  // User management functions
  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      alert("Please fill in all required fields");
      return;
    }

    if (newUser.password.length < 6) {
      alert("Password must be at least 6 characters long");
      return;
    }

    createUserMutation.mutate({
      ...newUser
    });
    toast({ description: `User created successfully! Password: ${newUser.password}` });
  };

  const handleEditUser = (user) => {
    setSelectedUser({
      ...user,
      originalRole: user.role,
      password: "" // Initialize empty password for optional reset
    });
    setIsEditUserDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    // Validate password if provided
    if (selectedUser.password && selectedUser.password.length > 0 && selectedUser.password.length < 6) {
      alert("Password must be at least 6 characters long");
      return;
    }

    const userId = selectedUser.id || selectedUser._id;
    const updateData = {
      name: selectedUser.name,
      email: selectedUser.email,
      role: selectedUser.role
    };

    // Include password in update if provided
    if (selectedUser.password && selectedUser.password.length > 0) {
      updateData.password = selectedUser.password;
    }

    updateUserMutationRQ.mutate({ id: userId, data: updateData });
    
    const successMessage = selectedUser.password && selectedUser.password.length > 0 
      ? `User updated successfully! New password: ${selectedUser.password}`
      : "User updated successfully!";
    toast({ description: successMessage });
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    const userId = userToDelete.id || userToDelete._id;
    deleteUserMutation.mutate(userId);
  };

  const getUserInitials = (name) => {
    return name?.split(' ').map(part => part[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Manage your account and application preferences</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className={`grid w-full ${
          user?.role === 'user' ? 'grid-cols-3' :
          isAdmin() ? 'grid-cols-11' : 'grid-cols-5'
        }`}>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          {user?.role !== 'user' && <TabsTrigger value="company">Company</TabsTrigger>}
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          {user?.role !== 'user' && <TabsTrigger value="preferences">Preferences</TabsTrigger>}
          <TabsTrigger value="security">Security</TabsTrigger>
          {isAdmin() && <TabsTrigger value="users">Users</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="work-items">Work Items</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="project-status">Project Status</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="production-stages">Production Stages</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="crews">Crews</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="api-keys">API Keys</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="database">Data Management</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="mr-2 h-5 w-5 text-blue-600" />
                Profile Information
              </CardTitle>
              <CardDescription>
                Update your personal information and contact details.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src="/placeholder-avatar.jpg" />
                  <AvatarFallback className="text-lg">{getUserInitials(user?.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold">{user?.name || 'User'}</h3>
                    <Badge className="capitalize">
                      {user?.role || 'user'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                    placeholder="Enter your email address"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <PhoneInput
                  id="phone"
                  value={profileData.phone}
                  onChange={(val) => setProfileData({...profileData, phone: val})}
                  placeholder="916.232.2332"
                />
              </div>
              
              <Button 
                onClick={handleSaveProfile} 
                disabled={saving.profile}
                className="w-full"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving.profile ? "Saving..." : "Save Profile Changes"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="company" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MapPin className="mr-2 h-5 w-5 text-blue-600" />
                Company Information
              </CardTitle>
              <CardDescription>
                Manage your business details and company settings.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input
                    id="companyName"
                    value={companyData.name}
                    onChange={(e) => setCompanyData({...companyData, name: e.target.value})}
                    placeholder="Enter company name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyPhone">Company Phone</Label>
                  <PhoneInput
                    id="companyPhone"
                    value={companyData.phone}
                    onChange={(val) => setCompanyData({...companyData, phone: val})}
                    placeholder="916.232.2332"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="companyEmail">Company Email</Label>
                  <Input
                    id="companyEmail"
                    type="email"
                    value={companyData.email}
                    onChange={(e) => setCompanyData({...companyData, email: e.target.value})}
                    placeholder="admin@yourcompany.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyLicense">CSLB License #</Label>
                  <Input
                    id="companyLicense"
                    value={companyData.license}
                    onChange={(e) => setCompanyData({...companyData, license: e.target.value})}
                    placeholder="#1234567"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={companyData.website}
                  onChange={(e) => setCompanyData({...companyData, website: e.target.value})}
                  placeholder="https://www.yourcompany.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Business Address</Label>
                <Textarea
                  id="address"
                  value={companyData.address}
                  onChange={(e) => setCompanyData({...companyData, address: e.target.value})}
                  placeholder="Enter your business address"
                  rows={3}
                />
              </div>
              
              <Button 
                onClick={handleSaveCompany} 
                disabled={saving.company}
                className="w-full"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving.company ? "Saving..." : "Save Company Information"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Mail className="mr-2 h-5 w-5 text-blue-600" />
                Notification Preferences
              </CardTitle>
              <CardDescription>
                Configure how and when you want to receive notifications.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Email Notifications</Label>
                    <p className="text-sm text-gray-600">Receive notifications via email</p>
                  </div>
                  <Switch 
                    checked={notifications.emailNotifications}
                    onCheckedChange={(checked) => setNotifications({...notifications, emailNotifications: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Push Notifications</Label>
                    <p className="text-sm text-gray-600">Receive push notifications in browser</p>
                  </div>
                  <Switch 
                    checked={notifications.pushNotifications}
                    onCheckedChange={(checked) => setNotifications({...notifications, pushNotifications: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">SMS Notifications</Label>
                    <p className="text-sm text-gray-600">Receive important updates via SMS</p>
                  </div>
                  <Switch 
                    checked={notifications.smsNotifications}
                    onCheckedChange={(checked) => setNotifications({...notifications, smsNotifications: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Weekly Reports</Label>
                    <p className="text-sm text-gray-600">Receive weekly summary reports</p>
                  </div>
                  <Switch 
                    checked={notifications.weeklyReports}
                    onCheckedChange={(checked) => setNotifications({...notifications, weeklyReports: checked})}
                  />
                </div>
              </div>
              
              <Button 
                onClick={handleSaveNotifications} 
                disabled={saving.notifications}
                className="w-full"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving.notifications ? "Saving..." : "Save Notification Settings"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preferences" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Search className="mr-2 h-5 w-5 text-blue-600" />
                Display Preferences
              </CardTitle>
              <CardDescription>
                Customize your application appearance and behavior.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="theme">Theme</Label>
                  <Select value={preferences.theme} onValueChange={(value) => setPreferences({...preferences, theme: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select value={preferences.timezone} onValueChange={(value) => setPreferences({...preferences, timezone: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/New_York">Eastern (ET)</SelectItem>
                      <SelectItem value="America/Chicago">Central (CT)</SelectItem>
                      <SelectItem value="America/Denver">Mountain (MT)</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific (PT)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dateFormat">Date Format</Label>
                  <Select value={preferences.dateFormat} onValueChange={(value) => setPreferences({...preferences, dateFormat: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select value={preferences.currency} onValueChange={(value) => setPreferences({...preferences, currency: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                      <SelectItem value="CAD">CAD (C$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button 
                onClick={handleSavePreferences} 
                disabled={saving.preferences}
                className="w-full"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving.preferences ? "Saving..." : "Save Preferences"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="mr-2 h-5 w-5 text-red-600" />
                Security Settings
              </CardTitle>
              <CardDescription>
                Manage your account security and password settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Password Change Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Change Password</h3>
                
                {passwordError && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-red-800 text-sm">{passwordError}</p>
                  </div>
                )}
                
                {passwordSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-3">
                    <p className="text-green-800 text-sm">{passwordSuccess}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      placeholder="Enter current password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      placeholder="Enter new password"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setPasswordData({currentPassword: "", newPassword: "", confirmPassword: ""});
                      setPasswordError("");
                      setPasswordSuccess("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleChangePassword} 
                    disabled={saving.password}
                    className="bg-brand-600 hover:bg-brand-700"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {saving.password ? "Updating..." : "Update Password"}
                  </Button>
                </div>
              </div>

              {/* Security Options */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="text-lg font-semibold">Security Options</h3>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Login Sessions</Label>
                    <p className="text-sm text-gray-500">View and manage your active login sessions</p>
                  </div>
                  <Button variant="outline" onClick={handleViewSessions}>
                    View Sessions
                  </Button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Two-Factor Authentication</Label>
                    <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
                  </div>
                  <Button variant="outline" disabled title="2FA implementation coming soon">
                    Enable 2FA
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Management Tab - Admin Only */}
        {isAdmin() && (
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="mr-2 h-5 w-5 text-blue-600" />
                  User Management
                </CardTitle>
                <CardDescription>
                  Manage user accounts and permissions (Admin only)
                </CardDescription>
                <div className="flex justify-end">
                  <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-brand-600 hover:bg-brand-700">
                        <UserPlus className="mr-2 h-4 w-4" />
                        Add New User
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>Add New User</DialogTitle>
                        <DialogDescription>
                          Create a new user account. Please set a password for the new user.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="user-name">Full Name *</Label>
                          <Input
                            id="user-name"
                            value={newUser.name}
                            onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                            placeholder="Enter full name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="user-email">Email Address *</Label>
                          <Input
                            id="user-email"
                            type="email"
                            value={newUser.email}
                            onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                            placeholder="Enter email address"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="user-password">Password *</Label>
                          <Input
                            id="user-password"
                            type="password"
                            value={newUser.password}
                            onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                            placeholder="Enter password (min 6 characters)"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="user-role">Role</Label>
                          <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setIsUserDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleCreateUser}
                          disabled={createUserMutation.isPending || !newUser.name || !newUser.email || !newUser.password}
                        >
                          {createUserMutation.isPending ? "Creating..." : "Create User"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {usersLoading ? (
                  <LoadingCard />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((userData) => (
                        <TableRow key={userData.id || userData._id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center space-x-2">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="text-xs">
                                  {getUserInitials(userData.name)}
                                </AvatarFallback>
                              </Avatar>
                              <span>{userData.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>{userData.email}</TableCell>
                          <TableCell>
                            <Badge className="capitalize">
                              {userData.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {userData.created_at ? new Date(userData.created_at).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end space-x-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleEditUser(userData)}
                                title="Edit user"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              {userData.role !== 'admin' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleDeleteUser(userData)}
                                  title="Delete user"
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {users.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                            No users found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Edit User Dialog */}
            <Dialog open={isEditUserDialogOpen} onOpenChange={setIsEditUserDialogOpen}>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Edit User</DialogTitle>
                  <DialogDescription>
                    Update user information, role, and optionally reset password.
                  </DialogDescription>
                </DialogHeader>
                {selectedUser && (
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-user-name">Full Name *</Label>
                      <Input
                        id="edit-user-name"
                        value={selectedUser.name}
                        onChange={(e) => setSelectedUser({...selectedUser, name: e.target.value})}
                        placeholder="Enter full name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-user-email">Email Address *</Label>
                      <Input
                        id="edit-user-email"
                        type="email"
                        value={selectedUser.email}
                        onChange={(e) => setSelectedUser({...selectedUser, email: e.target.value})}
                        placeholder="Enter email address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-user-password">New Password (Optional)</Label>
                      <Input
                        id="edit-user-password"
                        type="password"
                        value={selectedUser.password || ""}
                        onChange={(e) => setSelectedUser({...selectedUser, password: e.target.value})}
                        placeholder="Leave empty to keep current password"
                      />
                      <p className="text-xs text-gray-500">
                        Leave empty to keep the current password. Minimum 6 characters if changing.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-user-role">Role</Label>
                      <Select value={selectedUser.role} onValueChange={(value) => setSelectedUser({...selectedUser, role: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsEditUserDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUpdateUser}
                    disabled={updateUserMutationRQ.isPending || !selectedUser?.name || !selectedUser?.email}
                  >
                    {updateUserMutationRQ.isPending ? "Updating..." : "Update User"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete User Confirmation Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
              <DialogContent className="sm:max-w-[400px]">
                <DialogHeader>
                  <DialogTitle className="flex items-center text-red-600">
                    <Trash2 className="mr-2 h-5 w-5" />
                    Delete User
                  </DialogTitle>
                  <DialogDescription>
                    This action cannot be undone. This will permanently delete the user account and remove all associated data.
                  </DialogDescription>
                </DialogHeader>
                {userToDelete && (
                  <div className="py-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-red-100 text-red-600">
                            {getUserInitials(userToDelete.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold text-red-900">{userToDelete.name}</p>
                          <p className="text-sm text-red-700">{userToDelete.email}</p>
                          <Badge className="capitalize mt-1" variant="outline">
                            {userToDelete.role}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setIsDeleteDialogOpen(false);
                      setUserToDelete(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={confirmDeleteUser}
                    disabled={deleteUserMutation.isPending}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {deleteUserMutation.isPending ? "Deleting..." : "Delete User"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* Work Items Tab - Admin Only */}
        {isAdmin() && (
          <TabsContent value="work-items" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <PackagePlus className="mr-2 h-5 w-5 text-blue-600" />
                      Work Items
                    </CardTitle>
                    <CardDescription>
                      Manage work items, pricing, and categories for estimates.
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowArchivedWorkItems(!showArchivedWorkItems)}
                    >
                      {showArchivedWorkItems ? "Show Active" : "Show Archived"}
                    </Button>
                    <Button onClick={() => {
                      setEditingWorkItem(null);
                      setWorkItemFormData({ name: "", customer_view: "", category: "", unit: "ea.", price: 0, commission: 0, labor: 0, active: true });
                      setShowWorkItemDialog(true);
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Work Item
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search work items..."
                      value={workItemSearchTerm}
                      onChange={(e) => setWorkItemSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Work Items Table */}
                {workItemsLoading ? (
                  <div className="text-center py-8">Loading work items...</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Customer View</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead className="text-right">Commission</TableHead>
                        <TableHead className="text-right">Labor</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(workItemsData?.data || [])
                        .filter(item => 
                          item.name?.toLowerCase().includes(workItemSearchTerm.toLowerCase()) ||
                          item.category?.toLowerCase().includes(workItemSearchTerm.toLowerCase()) ||
                          item.customer_view?.toLowerCase().includes(workItemSearchTerm.toLowerCase())
                        )
                        .map((item) => (
                          <TableRow key={item.id || item._id} className={!item.active ? "opacity-50" : ""}>
                            <TableCell className="font-medium">{item.name}</TableCell>
                            <TableCell>{item.customer_view || "-"}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{item.category || "Uncategorized"}</Badge>
                            </TableCell>
                            <TableCell>{item.unit}</TableCell>
                            <TableCell className="text-right">${(item.price || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-right">${(item.commission || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-right">${(item.labor || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingWorkItem(item);
                                    setWorkItemFormData({
                                      name: item.name || "",
                                      customer_view: item.customer_view || "",
                                      category: item.category || "",
                                      unit: item.unit || "ea.",
                                      price: item.price || 0,
                                      commission: item.commission || 0,
                                      labor: item.labor || 0,
                                      active: item.active !== false
                                    });
                                    setShowWorkItemDialog(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {item.active !== false ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => archiveWorkItemMutation.mutate(item.id || item._id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => restoreWorkItemMutation.mutate(item.id || item._id)}
                                  >
                                    <ArchiveRestore className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                )}

                {/* Empty State */}
                {!workItemsLoading && (!workItemsData?.data || workItemsData.data.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <PackagePlus className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No work items found</p>
                    <p className="text-sm">Add work items to use them in estimates</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Work Item Dialog */}
            <Dialog open={showWorkItemDialog} onOpenChange={setShowWorkItemDialog}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingWorkItem ? "Edit Work Item" : "Add Work Item"}</DialogTitle>
                  <DialogDescription>
                    {editingWorkItem ? "Update the work item details below." : "Fill in the details for the new work item."}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={workItemFormData.name}
                      onChange={(e) => setWorkItemFormData({...workItemFormData, name: e.target.value})}
                      placeholder="e.g., Concrete Pavers"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Customer View Name</Label>
                    <Input
                      value={workItemFormData.customer_view}
                      onChange={(e) => setWorkItemFormData({...workItemFormData, customer_view: e.target.value})}
                      placeholder="Name shown to customers"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Input
                      value={workItemFormData.category}
                      onChange={(e) => setWorkItemFormData({...workItemFormData, category: e.target.value})}
                      placeholder="e.g., Hardscape, Plants"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Unit</Label>
                      <Select
                        value={workItemFormData.unit}
                        onValueChange={(value) => setWorkItemFormData({...workItemFormData, unit: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ea.">Each</SelectItem>
                          <SelectItem value="sq. ft.">Sq. Ft.</SelectItem>
                          <SelectItem value="ln. ft.">Linear Ft.</SelectItem>
                          <SelectItem value="cu. yd.">Cu. Yd.</SelectItem>
                          <SelectItem value="hr.">Hour</SelectItem>
                          <SelectItem value="lot">Lot</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Price ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={workItemFormData.price}
                        onChange={(e) => setWorkItemFormData({...workItemFormData, price: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Commission ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={workItemFormData.commission}
                        onChange={(e) => setWorkItemFormData({...workItemFormData, commission: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Labor ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={workItemFormData.labor}
                        onChange={(e) => setWorkItemFormData({...workItemFormData, labor: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowWorkItemDialog(false)}>Cancel</Button>
                  <Button 
                    onClick={() => {
                      if (editingWorkItem) {
                        updateWorkItemMutation.mutate({ id: editingWorkItem.id || editingWorkItem._id, data: workItemFormData });
                      } else {
                        createWorkItemMutation.mutate(workItemFormData);
                      }
                    }}
                    disabled={!workItemFormData.name || createWorkItemMutation.isPending || updateWorkItemMutation.isPending}
                  >
                    {(createWorkItemMutation.isPending || updateWorkItemMutation.isPending) ? "Saving..." : (editingWorkItem ? "Update" : "Create")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* Project Status Management Tab - Admin Only */}
        {isAdmin() && (
          <TabsContent value="project-status" className="space-y-6">
            {/* ========== Status Definitions Editor ========== */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <Palette className="mr-2 h-5 w-5 text-purple-600" />
                      Status Options
                    </CardTitle>
                    <CardDescription>
                      Add, edit, reorder, archive, or delete the statuses available for projects.
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => {
                      setEditingStatus(null);
                      setStatusFormData({
                        label: "",
                        value: "",
                        color: "bg-blue-500",
                        is_active: true,
                        sort_order: (activeStatusDefs.length + archivedStatusDefs.length) + 1,
                      });
                      setShowAddStatusDialog(true);
                    }}
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="mr-1 h-4 w-4" /> Add Status
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {statusDefsLoading ? (
                  <div className="text-center py-6 text-gray-500">Loading statuses...</div>
                ) : (
                  <>
                    {/* Active Statuses */}
                    <div className="space-y-2">
                      {activeStatusDefs
                        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                        .map((status) => {
                          const id = status.id || status._id;
                          return (
                            <div key={id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                              <div className="flex items-center gap-3">
                                <div className="flex flex-col gap-0.5">
                                  <button
                                    className="text-gray-400 hover:text-gray-600 p-0 h-4"
                                    onClick={() => {
                                      if ((status.sort_order || 0) <= 1) return;
                                      updateStatusDefMutation.mutate({ id, data: { sort_order: (status.sort_order || 0) - 1 } });
                                      // Also bump the one above
                                      const above = activeStatusDefs.find(s => (s.sort_order || 0) === (status.sort_order || 0) - 1);
                                      if (above) {
                                        const aboveId = above.id || above._id;
                                        updateStatusDefMutation.mutate({ id: aboveId, data: { sort_order: status.sort_order } });
                                      }
                                    }}
                                  >
                                    <ArrowUp className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    className="text-gray-400 hover:text-gray-600 p-0 h-4"
                                    onClick={() => {
                                      updateStatusDefMutation.mutate({ id, data: { sort_order: (status.sort_order || 0) + 1 } });
                                      const below = activeStatusDefs.find(s => (s.sort_order || 0) === (status.sort_order || 0) + 1);
                                      if (below) {
                                        const belowId = below.id || below._id;
                                        updateStatusDefMutation.mutate({ id: belowId, data: { sort_order: status.sort_order } });
                                      }
                                    }}
                                  >
                                    <ArrowDown className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <Badge className={`${status.color} text-white`}>{status.label}</Badge>
                                <span className="text-xs text-gray-400 font-mono">{status.value}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingStatus(status);
                                    setStatusFormData({
                                      label: status.label,
                                      value: status.value,
                                      color: status.color,
                                      is_active: status.is_active,
                                      sort_order: status.sort_order || 0,
                                    });
                                    setShowAddStatusDialog(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => archiveStatusDefMutation.mutate(id)}
                                  title="Archive"
                                >
                                  <ArchiveRestore className="h-4 w-4 text-orange-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    if (window.confirm(`Are you sure you want to permanently delete "${status.label}"?`)) {
                                      deleteStatusDefMutation.mutate(id);
                                    }
                                  }}
                                  title="Delete permanently"
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                    </div>

                    {/* Archived Statuses */}
                    {archivedStatusDefs.length > 0 && (
                      <div className="mt-4">
                        <button
                          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
                          onClick={() => setShowArchivedStatuses(!showArchivedStatuses)}
                        >
                          <ArchiveRestore className="h-4 w-4" />
                          {showArchivedStatuses ? 'Hide' : 'Show'} Archived Statuses ({archivedStatusDefs.length})
                        </button>
                        {showArchivedStatuses && (
                          <div className="space-y-2">
                            {archivedStatusDefs
                              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                              .map((status) => {
                                const id = status.id || status._id;
                                return (
                                  <div key={id} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50 opacity-75">
                                    <div className="flex items-center gap-3">
                                      <Badge variant="outline" className="text-gray-500">{status.label}</Badge>
                                      <span className="text-xs text-gray-400 font-mono">{status.value}</span>
                                      <span className="text-xs text-orange-500">(Archived)</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => restoreStatusDefMutation.mutate(id)}
                                        title="Restore"
                                      >
                                        <ArchiveRestore className="h-4 w-4 text-green-600" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          if (window.confirm(`Are you sure you want to permanently delete "${status.label}"?`)) {
                                            deleteStatusDefMutation.mutate(id);
                                          }
                                        }}
                                        title="Delete permanently"
                                      >
                                        <Trash2 className="h-4 w-4 text-red-500" />
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Add/Edit Status Dialog */}
            <Dialog open={showAddStatusDialog} onOpenChange={setShowAddStatusDialog}>
              <DialogContent className="sm:max-w-[450px]">
                <DialogHeader>
                  <DialogTitle>{editingStatus ? "Edit Status" : "Add New Status"}</DialogTitle>
                  <DialogDescription>
                    {editingStatus ? "Update the status name, color, and order." : "Create a new project status option."}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label>Label (Display Name)</Label>
                    <Input
                      value={statusFormData.label}
                      onChange={(e) => {
                        const label = e.target.value;
                        setStatusFormData(prev => ({
                          ...prev,
                          label,
                          // Auto-generate value slug from label when creating new
                          ...(!editingStatus ? { value: label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '') } : {}),
                        }));
                      }}
                      placeholder="e.g. Contract Signed"
                    />
                  </div>
                  <div>
                    <Label>Value (Internal Key)</Label>
                    <Input
                      value={statusFormData.value}
                      onChange={(e) => setStatusFormData(prev => ({ ...prev, value: e.target.value }))}
                      placeholder="e.g. contract_signed"
                      disabled={!!editingStatus}
                      className={editingStatus ? 'bg-gray-100' : ''}
                    />
                    {!editingStatus && (
                      <p className="text-xs text-gray-400 mt-1">Auto-generated from label. Cannot be changed after creation.</p>
                    )}
                  </div>
                  <div>
                    <Label>Color</Label>
                    <Select
                      value={statusFormData.color}
                      onValueChange={(value) => setStatusFormData(prev => ({ ...prev, color: value }))}
                    >
                      <SelectTrigger>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded ${statusFormData.color}`}></div>
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {statusColorOptions.map(c => (
                          <SelectItem key={c.value} value={c.value}>
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 rounded ${c.value}`}></div>
                              {c.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Sort Order</Label>
                    <Input
                      type="number"
                      value={statusFormData.sort_order}
                      onChange={(e) => setStatusFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label>Preview:</Label>
                    <Badge className={`${statusFormData.color} text-white`}>
                      {statusFormData.label || "Status Name"}
                    </Badge>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddStatusDialog(false)}>Cancel</Button>
                  <Button
                    disabled={!statusFormData.label || !statusFormData.value}
                    onClick={() => {
                      if (editingStatus) {
                        const id = editingStatus.id || editingStatus._id;
                        updateStatusDefMutation.mutate({
                          id,
                          data: {
                            label: statusFormData.label,
                            color: statusFormData.color,
                            is_active: statusFormData.is_active,
                            sort_order: statusFormData.sort_order,
                          }
                        });
                      } else {
                        createStatusDefMutation.mutate(statusFormData);
                      }
                    }}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {editingStatus ? "Save Changes" : "Add Status"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* ========== Existing Project Status Management ========== */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FolderOpen className="mr-2 h-5 w-5 text-blue-600" />
                  Project Status Management
                </CardTitle>
                <CardDescription>
                  Inline edit project status and dates. Click project name to view full details.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Controls Row */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  {/* Left side - Show per page */}
                  <div className="flex items-center gap-2">
                    <Label>Show:</Label>
                    <Select 
                      value={statusMgmtLimit.toString()} 
                      onValueChange={(value) => {
                        setStatusMgmtLimit(parseInt(value));
                        setStatusMgmtPage(1);
                      }}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-gray-500">per page</span>
                  </div>

                  {/* Center - Sort controls */}
                  <div className="flex items-center gap-2">
                    <Label>Sort by:</Label>
                    <Select 
                      value={statusMgmtSortBy} 
                      onValueChange={(value) => {
                        setStatusMgmtSortBy(value);
                        setStatusMgmtPage(1);
                      }}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusMgmtSortOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select 
                      value={statusMgmtSortOrder} 
                      onValueChange={(value) => {
                        setStatusMgmtSortOrder(value);
                        setStatusMgmtPage(1);
                      }}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">Ascending</SelectItem>
                        <SelectItem value="desc">Descending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Right side - Total count */}
                  {statusMgmtData?.data?.pagination && (
                    <div className="text-sm text-gray-500">
                      Total: {statusMgmtData.data.pagination.total} projects
                    </div>
                  )}
                </div>

                {/* Projects Table with Inline Editing */}
                {statusMgmtLoading ? (
                  <div className="text-center py-8">Loading projects...</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[200px]">Project Name</TableHead>
                          <TableHead className="min-w-[150px]">Status</TableHead>
                          <TableHead className="min-w-[140px]">Contract Signed</TableHead>
                          <TableHead className="min-w-[140px]">Start Date</TableHead>
                          <TableHead className="min-w-[140px]">End Date</TableHead>
                          <TableHead className="min-w-[140px]">Status Changed</TableHead>
                          <TableHead className="w-[100px] text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {statusMgmtData?.data?.projects?.map((project) => {
                          const isEditing = editingProjectId === project.id;
                          
                          return (
                            <TableRow key={project.id}>
                              {/* Project Name - Clickable */}
                              <TableCell className="font-medium">
                                <button
                                  className="text-left text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                  onClick={() => {
                                    setSelectedProjectId(project.id);
                                    setIsProjectDetailOpen(true);
                                  }}
                                >
                                  {project.name}
                                </button>
                                <div className="text-xs text-gray-500 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {project.project_type === 'construction' ? 'Construction' : 'Design Only'}
                                  </Badge>
                                </div>
                              </TableCell>
                              
                              {/* Status - Editable Dropdown */}
                              <TableCell>
                                {isEditing ? (
                                  <Select 
                                    value={editingValues.status || project.status}
                                    onValueChange={(value) => setEditingValues(prev => ({ ...prev, status: value }))}
                                  >
                                    <SelectTrigger className="w-[140px]">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {projectStatusOptions.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Badge className={`${allStatusDefs.find(s => s.value === project.status)?.color || 'bg-blue-500'} text-white`}>
                                    {allStatusDefs.find(s => s.value === project.status)?.label || project.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </Badge>
                                )}
                              </TableCell>
                              
                              {/* Contract Signed Date - Editable */}
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    type="date"
                                    value={editingValues.contract_sign_date ?? (project.contract_sign_date?.split('T')[0] || '')}
                                    onChange={(e) => setEditingValues(prev => ({ ...prev, contract_sign_date: e.target.value }))}
                                    className="w-[130px]"
                                  />
                                ) : (
                                  <span className="text-sm">
                                    {project.contract_sign_date ? formatDatePST(project.contract_sign_date) : '-'}
                                  </span>
                                )}
                              </TableCell>
                              
                              {/* Start Date - Editable */}
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    type="date"
                                    value={editingValues.start_date ?? (project.start_date?.split('T')[0] || '')}
                                    onChange={(e) => setEditingValues(prev => ({ ...prev, start_date: e.target.value }))}
                                    className="w-[130px]"
                                  />
                                ) : (
                                  <span className="text-sm">
                                    {project.start_date ? formatDatePST(project.start_date) : '-'}
                                  </span>
                                )}
                              </TableCell>
                              
                              {/* End Date - Editable */}
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    type="date"
                                    value={editingValues.end_date ?? (project.end_date?.split('T')[0] || '')}
                                    onChange={(e) => setEditingValues(prev => ({ ...prev, end_date: e.target.value }))}
                                    className="w-[130px]"
                                  />
                                ) : (
                                  <span className="text-sm">
                                    {project.end_date ? formatDatePST(project.end_date) : '-'}
                                  </span>
                                )}
                              </TableCell>
                              
                              {/* Status Changed Date - Editable */}
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    type="date"
                                    value={editingValues.status_changed_at ?? (project.status_changed_at?.split('T')[0] || project.updated_at?.split('T')[0] || '')}
                                    onChange={(e) => setEditingValues(prev => ({ ...prev, status_changed_at: e.target.value }))}
                                    className="w-[130px]"
                                  />
                                ) : (
                                  <span className="text-sm">
                                    {project.status_changed_at 
                                      ? formatDatePST(project.status_changed_at)
                                      : project.updated_at 
                                        ? formatDatePST(project.updated_at)
                                        : '-'
                                    }
                                  </span>
                                )}
                              </TableCell>
                              
                              {/* Actions */}
                              <TableCell className="text-right">
                                {isEditing ? (
                                  <div className="flex justify-end gap-1">
                                    <Button 
                                      size="sm" 
                                      variant="default"
                                      className="h-8 w-8 p-0"
                                      onClick={() => {
                                        // Build updates object only with changed values
                                        const updates = {};
                                        if (editingValues.status !== undefined && editingValues.status !== project.status) {
                                          updates.status = editingValues.status;
                                        }
                                        if (editingValues.contract_sign_date !== undefined) {
                                          updates.contract_sign_date = editingValues.contract_sign_date;
                                        }
                                        if (editingValues.start_date !== undefined) {
                                          updates.start_date = editingValues.start_date;
                                        }
                                        if (editingValues.end_date !== undefined) {
                                          updates.end_date = editingValues.end_date;
                                        }
                                        if (editingValues.status_changed_at !== undefined) {
                                          updates.status_changed_at = editingValues.status_changed_at;
                                        }
                                        
                                        if (Object.keys(updates).length > 0) {
                                          updateStatusDateMutation.mutate({
                                            projectId: project.id,
                                            updates
                                          });
                                        } else {
                                          setEditingProjectId(null);
                                          setEditingValues({});
                                        }
                                      }}
                                      disabled={updateStatusDateMutation.isPending}
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="h-8 w-8 p-0"
                                      onClick={() => {
                                        setEditingProjectId(null);
                                        setEditingValues({});
                                      }}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      setEditingProjectId(project.id);
                                      setEditingValues({});
                                    }}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Pagination */}
                {statusMgmtData?.data?.pagination && statusMgmtData.data.pagination.total_pages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setStatusMgmtPage(p => Math.max(1, p - 1))}
                      disabled={statusMgmtPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {statusMgmtPage} of {statusMgmtData.data.pagination.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setStatusMgmtPage(p => Math.min(statusMgmtData.data.pagination.total_pages, p + 1))}
                      disabled={statusMgmtPage === statusMgmtData.data.pagination.total_pages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Project Detail Dialog */}
            {selectedProjectId && (
              <ProjectDetailDialog
                selectedProject={selectedProjectData}
                isDetailDialogOpen={isProjectDetailOpen}
                setIsDetailDialogOpen={(open) => {
                  setIsProjectDetailOpen(open);
                  if (!open) {
                    setSelectedProjectId(null);
                    // Refresh the list in case something changed
                    queryClient.invalidateQueries({ queryKey: ['project-status-management'] });
                  }
                }}
                loadingProjectDetails={loadingProjectDetails}
                designers={[]}
                subcontractors={[]}
                onProjectUpdated={() => {
                  queryClient.invalidateQueries({ queryKey: ['project-status-management'] });
                  queryClient.invalidateQueries({ queryKey: ['project-detail', selectedProjectId] });
                }}
              />
            )}
          </TabsContent>
        )}

        {/* Production Stages Tab - Admin Only */}
        {isAdmin() && (
          <TabsContent value="production-stages" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <FolderOpen className="mr-2 h-5 w-5 text-blue-600" />
                      Production Stages
                    </CardTitle>
                    <CardDescription>
                      Define the stages projects move through during installation. Used by the Production kanban board.
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => {
                      setEditingStage(null);
                      setStageFormData({
                        value: "",
                        label: "",
                        color: "#64748b",
                        description: "",
                        sort_order: (productionStagesData?.data?.length || 0) + 1,
                        is_active: true,
                      });
                      setShowStageDialog(true);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Stage
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {productionStagesLoading ? (
                  <div className="text-center py-8">Loading stages...</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[60px]">Order</TableHead>
                          <TableHead className="w-[60px]">Color</TableHead>
                          <TableHead>Label</TableHead>
                          <TableHead>Value</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="w-[80px]">Active</TableHead>
                          <TableHead className="w-[120px] text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {/* Standard stages */}
                        {(productionStagesData?.data || []).filter(s => !s.is_optional).map((stage) => (
                          <TableRow key={stage.id}>
                            <TableCell>{stage.sort_order}</TableCell>
                            <TableCell>
                              <div
                                className="w-6 h-6 rounded border"
                                style={{ backgroundColor: stage.color || '#64748b' }}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{stage.label}</TableCell>
                            <TableCell className="text-xs text-gray-500">{stage.value}</TableCell>
                            <TableCell className="text-sm">{stage.description || '-'}</TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                {stage.is_active ? (
                                  <Badge className="bg-green-500 text-white">Active</Badge>
                                ) : (
                                  <Badge variant="outline">Inactive</Badge>
                                )}
                                {stage.is_optional && (
                                  <Badge className="bg-amber-500 text-white">Optional</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-8 p-0"
                                  onClick={() => {
                                    setEditingStage(stage);
                                    setStageFormData({
                                      value: stage.value || "",
                                      label: stage.label || "",
                                      color: stage.color || "#64748b",
                                      description: stage.description || "",
                                      sort_order: stage.sort_order || 0,
                                      is_active: stage.is_active !== false,
                                      is_optional: stage.is_optional || false,
                                    });
                                    setShowStageDialog(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-8 p-0 text-red-600"
                                  onClick={() => {
                                    if (window.confirm(`Delete stage "${stage.label}"?`)) {
                                      deleteStageMutation.mutate(stage.id);
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* Separator between standard and optional */}
                        {(productionStagesData?.data || []).some(s => s.is_optional) && (
                          <TableRow>
                            <TableCell colSpan={7} className="bg-amber-50 text-amber-800 font-semibold text-sm py-2 px-4">
                              Optional Stages (added per project as needed)
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Optional stages */}
                        {(productionStagesData?.data || []).filter(s => s.is_optional).map((stage) => (
                          <TableRow key={stage.id} className="bg-amber-50/30">
                            <TableCell>{stage.sort_order}</TableCell>
                            <TableCell>
                              <div
                                className="w-6 h-6 rounded border"
                                style={{ backgroundColor: stage.color || '#64748b' }}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{stage.label}</TableCell>
                            <TableCell className="text-xs text-gray-500">{stage.value}</TableCell>
                            <TableCell className="text-sm">{stage.description || '-'}</TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                {stage.is_active ? (
                                  <Badge className="bg-green-500 text-white">Active</Badge>
                                ) : (
                                  <Badge variant="outline">Inactive</Badge>
                                )}
                                <Badge className="bg-amber-500 text-white">Optional</Badge>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-8 p-0"
                                  onClick={() => {
                                    setEditingStage(stage);
                                    setStageFormData({
                                      value: stage.value || "",
                                      label: stage.label || "",
                                      color: stage.color || "#64748b",
                                      description: stage.description || "",
                                      sort_order: stage.sort_order || 0,
                                      is_active: stage.is_active !== false,
                                      is_optional: stage.is_optional || false,
                                    });
                                    setShowStageDialog(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-8 p-0 text-red-600"
                                  onClick={() => {
                                    if (window.confirm(`Delete stage "${stage.label}"?`)) {
                                      deleteStageMutation.mutate(stage.id);
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stage Edit/Create Dialog */}
            <Dialog open={showStageDialog} onOpenChange={setShowStageDialog}>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>{editingStage ? 'Edit Stage' : 'Add Stage'}</DialogTitle>
                  <DialogDescription>
                    Configure a production workflow stage.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="stage-label">Label *</Label>
                    <Input
                      id="stage-label"
                      value={stageFormData.label}
                      onChange={(e) => setStageFormData(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="e.g. Site Prep"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="stage-value">Value (slug) *</Label>
                    <Input
                      id="stage-value"
                      value={stageFormData.value}
                      onChange={(e) => setStageFormData(prev => ({ ...prev, value: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                      placeholder="e.g. site_prep"
                      disabled={!!editingStage}
                    />
                    {!editingStage && (
                      <p className="text-xs text-gray-500">Lowercase, underscores. Cannot be changed later.</p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="stage-description">Description</Label>
                    <Textarea
                      id="stage-description"
                      value={stageFormData.description}
                      onChange={(e) => setStageFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="stage-color">Color</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="stage-color"
                          type="color"
                          value={stageFormData.color}
                          onChange={(e) => setStageFormData(prev => ({ ...prev, color: e.target.value }))}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={stageFormData.color}
                          onChange={(e) => setStageFormData(prev => ({ ...prev, color: e.target.value }))}
                          placeholder="#64748b"
                        />
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="stage-sort-order">Sort Order</Label>
                      <Input
                        id="stage-sort-order"
                        type="number"
                        value={stageFormData.sort_order}
                        onChange={(e) => setStageFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="stage-active"
                        checked={stageFormData.is_active}
                        onCheckedChange={(checked) => setStageFormData(prev => ({ ...prev, is_active: checked }))}
                      />
                      <Label htmlFor="stage-active">Active</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="stage-optional"
                        checked={stageFormData.is_optional || false}
                        onCheckedChange={(checked) => setStageFormData(prev => ({ ...prev, is_optional: checked }))}
                      />
                      <Label htmlFor="stage-optional">Optional (per-project add-on)</Label>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowStageDialog(false)}>Cancel</Button>
                  <Button
                    onClick={() => {
                      if (!stageFormData.label || !stageFormData.value) {
                        toast({ variant: "destructive", description: "Label and value are required" });
                        return;
                      }
                      if (editingStage) {
                        const { value, ...updateData } = stageFormData;
                        updateStageMutation.mutate({ id: editingStage.id, data: updateData });
                      } else {
                        createStageMutation.mutate(stageFormData);
                      }
                    }}
                    disabled={createStageMutation.isPending || updateStageMutation.isPending}
                  >
                    {editingStage ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* Crews Tab - Admin Only */}
        {isAdmin() && (
          <TabsContent value="crews" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <Users className="mr-2 h-5 w-5 text-blue-600" />
                      Install Crews
                    </CardTitle>
                    <CardDescription>
                      Manage in-house install crews. Assign crews to production stages on the kanban board.
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => {
                      setEditingCrew(null);
                      setCrewFormData({
                        name: "",
                        color: "#2563eb",
                        members: [],
                        notes: "",
                        is_active: true,
                        is_hourly: true,
                      });
                      setShowCrewDialog(true);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Crew
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {crewsLoading ? (
                  <div className="text-center py-8">Loading crews...</div>
                ) : (crewsData?.data || []).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No crews yet. Click "Add Crew" to create your first install crew.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[60px]">Color</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Lead</TableHead>
                          <TableHead className="w-[80px]">Members</TableHead>
                          <TableHead className="w-[80px]">Active</TableHead>
                          <TableHead className="w-[120px] text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(crewsData?.data || []).map((crew) => (
                          <TableRow key={crew.id}>
                            <TableCell>
                              <div
                                className="w-6 h-6 rounded border"
                                style={{ backgroundColor: crew.color || '#2563eb' }}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{crew.name}</TableCell>
                            <TableCell>{(() => { const lead = (crew.members || []).find(m => m.position === 'Lead/Foreman'); return lead ? `${lead.name || ''}${lead.last_name ? ' ' + lead.last_name : ''}`.trim() || '-' : '-'; })()}</TableCell>
                            <TableCell>{(crew.members || []).length}</TableCell>
                            <TableCell>
                              {crew.is_active ? (
                                <Badge className="bg-green-500 text-white">Active</Badge>
                              ) : (
                                <Badge variant="outline">Inactive</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-8 p-0"
                                  onClick={() => {
                                    setEditingCrew(crew);
                                    setCrewFormData({
                                      name: crew.name || "",
                                      color: crew.color || "#2563eb",
                                      members: (crew.members || []).map(m => ({
                                        ...m,
                                        position: m.position || m.role || "",
                                      })),
                                      notes: crew.notes || "",
                                      is_active: crew.is_active !== false,
                                      is_hourly: crew.is_hourly !== false,
                                    });
                                    setShowCrewDialog(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-8 p-0 text-red-600"
                                  onClick={() => {
                                    if (window.confirm(`Delete crew "${crew.name}"?`)) {
                                      deleteCrewMutation.mutate(crew.id);
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Crew Edit/Create Dialog */}
            <Dialog open={showCrewDialog} onOpenChange={setShowCrewDialog}>
              <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingCrew ? 'Edit Crew' : 'Add Crew'}</DialogTitle>
                  <DialogDescription>
                    Configure an install crew and its members.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="crew-name">Crew Name *</Label>
                    <Input
                      id="crew-name"
                      value={crewFormData.name}
                      onChange={(e) => setCrewFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Crew Alpha"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="crew-color">Color</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="crew-color"
                        type="color"
                        value={crewFormData.color}
                        onChange={(e) => setCrewFormData(prev => ({ ...prev, color: e.target.value }))}
                        className="w-16 h-10 p-1"
                      />
                      <Input
                        value={crewFormData.color}
                        onChange={(e) => setCrewFormData(prev => ({ ...prev, color: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Members */}
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between">
                      <Label>Crew Members</Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setCrewFormData(prev => ({
                          ...prev,
                          members: [...(prev.members || []), { name: "", last_name: "", position: "", phone: "", hourly_wage: null }]
                        }))}
                      >
                        <Plus className="mr-1 h-3 w-3" /> Add Member
                      </Button>
                    </div>
                    {(crewFormData.members || []).map((member, idx) => (
                      <div key={idx} className={`grid gap-2 items-start ${crewFormData.is_hourly ? 'grid-cols-[1fr_1fr_1fr_0.8fr_0.6fr_auto]' : 'grid-cols-[1fr_1fr_1fr_0.8fr_auto]'}`}>
                        <Input
                          placeholder="First Name"
                          value={member.name || ""}
                          onChange={(e) => {
                            const updated = [...crewFormData.members];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            setCrewFormData(prev => ({ ...prev, members: updated }));
                          }}
                        />
                        <Input
                          placeholder="Last Name"
                          value={member.last_name || ""}
                          onChange={(e) => {
                            const updated = [...crewFormData.members];
                            updated[idx] = { ...updated[idx], last_name: e.target.value };
                            setCrewFormData(prev => ({ ...prev, members: updated }));
                          }}
                        />
                        <Select
                          value={member.position || undefined}
                          onValueChange={(value) => {
                            const updated = [...crewFormData.members];
                            updated[idx] = { ...updated[idx], position: value };
                            setCrewFormData(prev => ({ ...prev, members: updated }));
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Position" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Lead/Foreman">Lead/Foreman</SelectItem>
                            <SelectItem value="Foreman / Crew Lead">Foreman / Crew Lead</SelectItem>
                            <SelectItem value="Journeyman">Journeyman</SelectItem>
                            <SelectItem value="Installer">Installer</SelectItem>
                            <SelectItem value="Laborer">Laborer</SelectItem>
                          </SelectContent>
                        </Select>
                        <PhoneInput
                          placeholder="Phone"
                          value={member.phone || ""}
                          onChange={(value) => {
                            const updated = [...crewFormData.members];
                            updated[idx] = { ...updated[idx], phone: value };
                            setCrewFormData(prev => ({ ...prev, members: updated }));
                          }}
                        />
                        {crewFormData.is_hourly && (
                          <Input
                            type="number"
                            placeholder="$/hr"
                            value={member.hourly_wage || ""}
                            onChange={(e) => {
                              const updated = [...crewFormData.members];
                              updated[idx] = { ...updated[idx], hourly_wage: e.target.value ? parseFloat(e.target.value) : null };
                              setCrewFormData(prev => ({ ...prev, members: updated }));
                            }}
                          />
                        )}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-10 w-10 p-0 text-red-600"
                          onClick={() => {
                            const updated = crewFormData.members.filter((_, i) => i !== idx);
                            setCrewFormData(prev => ({ ...prev, members: updated }));
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="crew-notes">Notes</Label>
                    <Textarea
                      id="crew-notes"
                      value={crewFormData.notes}
                      onChange={(e) => setCrewFormData(prev => ({ ...prev, notes: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="crew-active"
                        checked={crewFormData.is_active}
                        onCheckedChange={(checked) => setCrewFormData(prev => ({ ...prev, is_active: checked }))}
                      />
                      <Label htmlFor="crew-active">Active</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="crew-hourly"
                        checked={crewFormData.is_hourly}
                        onCheckedChange={(checked) => {
                          setCrewFormData(prev => ({
                            ...prev,
                            is_hourly: checked,
                            // Clear wages when switching to non-hourly
                            members: checked
                              ? prev.members
                              : (prev.members || []).map(m => ({ ...m, hourly_wage: null })),
                          }));
                        }}
                      />
                      <Label htmlFor="crew-hourly">
                        {crewFormData.is_hourly ? "Hourly Crew" : "Non-Hourly (Subcontractor)"}
                      </Label>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCrewDialog(false)}>Cancel</Button>
                  <Button
                    onClick={() => {
                      if (!crewFormData.name) {
                        toast({ variant: "destructive", description: "Crew name is required" });
                        return;
                      }
                      // Normalize payload: empty strings on optional fields → null
                      // Pydantic rejects "" for Optional[float]
                      const normalized = {
                        ...crewFormData,
                        members: (crewFormData.members || []).map(m => ({
                          name: m.name || "",
                          last_name: m.last_name || null,
                          position: m.position || null,
                          phone: m.phone || null,
                          // Non-hourly crews always send null wage
                          hourly_wage: !crewFormData.is_hourly
                            ? null
                            : (m.hourly_wage === "" || m.hourly_wage === undefined)
                              ? null
                              : (typeof m.hourly_wage === "string" ? parseFloat(m.hourly_wage) || null : m.hourly_wage),
                        })),
                      };
                      if (editingCrew) {
                        updateCrewMutation.mutate({ id: editingCrew.id, data: normalized });
                      } else {
                        createCrewMutation.mutate(normalized);
                      }
                    }}
                    disabled={createCrewMutation.isPending || updateCrewMutation.isPending}
                  >
                    {editingCrew ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* API Keys Tab - Admin Only */}
        {isAdmin() && (
          <TabsContent value="api-keys" className="space-y-6">
            <ApiKeysTab />
          </TabsContent>
        )}

        {/* Data Management Tab - Admin Only */}
        {isAdmin() && (
          <TabsContent value="database" className="space-y-6">
            <ImportExportSection />

            {/* Proposal Tool Sync */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Database className="mr-2 h-5 w-5" />
                  Sync Customers to Proposal Tool
                </CardTitle>
                <CardDescription>
                  Push CRM customers to the Proposal Tool. The Proposal Tool dedupes by email, so
                  existing customers are safely updated and missing ones are created. Customers
                  without an email are skipped.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={handleProposalDryRun} disabled={proposalSyncing}>
                    {proposalSyncing ? "Checking..." : "Preview (Dry Run)"}
                  </Button>
                  {proposalSyncResult && proposalSyncResult.missing_count > 0 && (
                    <Button onClick={handleProposalSync} disabled={proposalSyncing} className="bg-brand-600 hover:bg-brand-700">
                      {proposalSyncing ? "Sending..." : `Add ${proposalSyncResult.missing_count} Missing Customers`}
                    </Button>
                  )}
                </div>

                {/* Dry run result */}
                {proposalSyncResult && (
                  <div className="rounded-lg border bg-gray-50 p-3 text-sm space-y-1">
                    <div className="font-semibold">Preview results:</div>
                    <div>Total CRM customers: <strong>{proposalSyncResult.total_customers}</strong></div>
                    <div className="text-gray-600">Already in Proposal Tool (skipped): <strong>{proposalSyncResult.already_in_proposal_count}</strong></div>
                    <div className="text-green-700">Missing — will be added: <strong>{proposalSyncResult.missing_count}</strong></div>
                    <div className="text-amber-700">Without email (skipped): <strong>{proposalSyncResult.without_email_count}</strong></div>
                    {proposalSyncResult.check_failed_count > 0 && (
                      <div className="text-red-700">Couldn't verify (skipped to be safe): <strong>{proposalSyncResult.check_failed_count}</strong></div>
                    )}
                    {proposalSyncResult.missing_count > 0 && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-xs text-gray-600">View who would be added</summary>
                        <div className="text-xs text-gray-500 mt-1 max-h-40 overflow-auto">
                          {(proposalSyncResult.missing_sample || []).map((m, i) => <div key={i}>{m}</div>)}
                          {proposalSyncResult.missing_count > 50 && <div>…</div>}
                        </div>
                      </details>
                    )}
                    {proposalSyncResult.without_email_count > 0 && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-xs text-gray-600">View skipped (no email)</summary>
                        <div className="text-xs text-gray-500 mt-1 max-h-40 overflow-auto">
                          {(proposalSyncResult.without_email_sample || []).join(", ")}
                        </div>
                      </details>
                    )}
                    {proposalSyncResult.missing_count === 0 ? (
                      <p className="text-xs text-green-700 pt-1 font-medium">Everything is already in sync — nothing to add.</p>
                    ) : (
                      <p className="text-xs text-gray-500 pt-1">Nothing has been sent yet. Click "Add … Missing Customers" to run for real.</p>
                    )}
                  </div>
                )}

                {/* Real run result */}
                {proposalSentResult && (
                  <div className="rounded-lg border border-green-300 bg-green-50 p-3 text-sm space-y-1">
                    <div className="font-semibold text-green-900">Sync complete</div>
                    <div className="text-green-800">Sent: <strong>{proposalSentResult.sent}</strong></div>
                    {proposalSentResult.failed_count > 0 && (
                      <>
                        <div className="text-red-700">Failed: <strong>{proposalSentResult.failed_count}</strong></div>
                        <details className="mt-1">
                          <summary className="cursor-pointer text-xs text-gray-600">View failures</summary>
                          <div className="text-xs text-gray-500 mt-1 max-h-40 overflow-auto">
                            {(proposalSentResult.failed || []).map((f, i) => (
                              <div key={i}>{f.name} &lt;{f.email}&gt; — {f.error}</div>
                            ))}
                          </div>
                        </details>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Database className="mr-2 h-5 w-5" />
                  Database Management
                </CardTitle>
                <CardDescription>
                  Clear database collections while preserving configuration data.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Collections Status */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Collections Status</h3>
                  {collectionsLoading && <div>Loading collections status...</div>}
                  
                  {collectionsData?.data && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {Object.entries(collectionsData.data).map(([collection, status]) => (
                        <div key={collection} className="flex justify-between items-center p-3 border rounded-lg">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium capitalize">{collection.replace('_', ' ')}</span>
                            {status.will_be_cleared ? (
                              <Badge variant="destructive">Will be cleared</Badge>
                            ) : (
                              <Badge variant="secondary">Preserved</Badge>
                            )}
                          </div>
                          <span className="text-sm text-gray-600">{status.count} items</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Specific Collection Deletions */}
                <div className="border-t pt-6 space-y-4">
                  <h3 className="text-lg font-semibold">Delete Specific Collections</h3>
                  <p className="text-sm text-gray-600">
                    Delete all items from specific configuration collections. Use these options carefully.
                  </p>

                  {/* Delete Email Templates */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-orange-50 border-orange-200">
                    <div>
                      <h4 className="font-medium text-orange-900">Delete All Email Templates</h4>
                      <p className="text-sm text-orange-700">
                        Remove all email templates ({collectionsData?.data?.email_templates?.count || 0} templates)
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-100"
                      onClick={() => setIsDeleteEmailTemplatesDialogOpen(true)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Templates
                    </Button>
                  </div>

                  {/* Delete Work Items */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-orange-50 border-orange-200">
                    <div>
                      <h4 className="font-medium text-orange-900">Delete All Work Items</h4>
                      <p className="text-sm text-orange-700">
                        Remove all work items ({collectionsData?.data?.work_items?.count || 0} items)
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-100"
                      onClick={() => setIsDeleteWorkItemsDialogOpen(true)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Work Items
                    </Button>
                  </div>

                  {/* Delete Project Statuses */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-orange-50 border-orange-200">
                    <div>
                      <h4 className="font-medium text-orange-900">Delete All Project Statuses</h4>
                      <p className="text-sm text-orange-700">
                        Remove all project statuses ({collectionsData?.data?.project_statuses?.count || 0} statuses)
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-100"
                      onClick={() => setIsDeleteProjectStatusesDialogOpen(true)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Statuses
                    </Button>
                  </div>

                  {/* Delete All Vendors */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-orange-50 border-orange-200">
                    <div>
                      <h4 className="font-medium text-orange-900">Delete All Vendors</h4>
                      <p className="text-sm text-orange-700">
                        Remove all vendors ({collectionsData?.data?.vendors?.count || 0} vendors)
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-100"
                      onClick={() => setIsDeleteVendorsDialogOpen(true)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Vendors
                    </Button>
                  </div>
                </div>

                {/* Clear Database Section */}
                <div className="border-t pt-6">
                  <div className="flex items-start space-x-4">
                    <AlertTriangle className="h-6 w-6 text-red-500 mt-1" />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-red-900">Clear Database</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        This will permanently delete all customer data, projects, estimates, and schedules.
                        <br />
                        <strong>Preserved:</strong> Email templates, automations, users, and project statuses.
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <Button 
                      variant="destructive" 
                      onClick={() => setIsDatabaseClearDialogOpen(true)}
                      className="flex items-center"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Clear Database
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Clear Database Confirmation Dialog */}
            <Dialog 
              open={isDatabaseClearDialogOpen} 
              onOpenChange={(open) => {
                setIsDatabaseClearDialogOpen(open);
                if (!open) {
                  setConfirmationText(""); // Clear confirmation text when dialog closes
                }
              }}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center text-red-600">
                    <AlertTriangle className="mr-2 h-5 w-5" />
                    Clear Database
                  </DialogTitle>
                  <DialogDescription>
                    Are you absolutely sure you want to clear the database? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="py-4 space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                    <h4 className="font-semibold text-red-800">This will permanently delete:</h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• All customers and their notes</li>
                      <li>• All projects and estimates</li>
                      <li>• All scheduled items</li>
                      <li>• All designers and subcontractors</li>
                      <li>• All leads and email logs</li>
                    </ul>
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                    <h4 className="font-semibold text-green-800">This will preserve:</h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li>• All email templates</li>
                      <li>• All automations</li>
                      <li>• All user accounts</li>
                      <li>• Project status configurations</li>
                    </ul>
                  </div>

                  <div className="border-t pt-4">
                    <Label htmlFor="confirmationInput" className="text-sm font-semibold text-gray-900">
                      To confirm, type "Yes Clear Database" in the box below:
                    </Label>
                    <Input
                      id="confirmationInput"
                      value={confirmationText}
                      onChange={(e) => setConfirmationText(e.target.value)}
                      placeholder="Yes Clear Database"
                      className="mt-2"
                      disabled={clearDatabaseMutation.isPending}
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setIsDatabaseClearDialogOpen(false);
                      setConfirmationText(""); // Clear confirmation text
                    }}
                    disabled={clearDatabaseMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => {
                      clearDatabaseMutation.mutate();
                    }}
                    disabled={clearDatabaseMutation.isPending || confirmationText !== "Yes Clear Database"}
                  >
                    {clearDatabaseMutation.isPending ? "Clearing..." : "Yes, Clear Database"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete Email Templates Confirmation Dialog */}
            <Dialog open={isDeleteEmailTemplatesDialogOpen} onOpenChange={setIsDeleteEmailTemplatesDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center text-orange-600">
                    <AlertTriangle className="mr-2 h-5 w-5" />
                    Delete All Email Templates
                  </DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete all email templates? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <p className="text-sm text-orange-800">
                      This will permanently delete <strong>{collectionsData?.data?.email_templates?.count || 0} email templates</strong> from your database.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsDeleteEmailTemplatesDialogOpen(false)}
                    disabled={deleteEmailTemplatesMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => deleteEmailTemplatesMutation.mutate()}
                    disabled={deleteEmailTemplatesMutation.isPending}
                  >
                    {deleteEmailTemplatesMutation.isPending ? "Deleting..." : "Delete All Templates"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete Work Items Confirmation Dialog */}
            <Dialog open={isDeleteWorkItemsDialogOpen} onOpenChange={setIsDeleteWorkItemsDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center text-orange-600">
                    <AlertTriangle className="mr-2 h-5 w-5" />
                    Delete All Work Items
                  </DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete all work items? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <p className="text-sm text-orange-800">
                      This will permanently delete <strong>{collectionsData?.data?.work_items?.count || 0} work items</strong> from your database.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsDeleteWorkItemsDialogOpen(false)}
                    disabled={deleteWorkItemsMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => deleteWorkItemsMutation.mutate()}
                    disabled={deleteWorkItemsMutation.isPending}
                  >
                    {deleteWorkItemsMutation.isPending ? "Deleting..." : "Delete All Work Items"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete Project Statuses Confirmation Dialog */}
            <Dialog open={isDeleteProjectStatusesDialogOpen} onOpenChange={setIsDeleteProjectStatusesDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center text-orange-600">
                    <AlertTriangle className="mr-2 h-5 w-5" />
                    Delete All Project Statuses
                  </DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete all project statuses? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <p className="text-sm text-orange-800">
                      This will permanently delete <strong>{collectionsData?.data?.project_statuses?.count || 0} project statuses</strong> from your database.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsDeleteProjectStatusesDialogOpen(false)}
                    disabled={deleteProjectStatusesMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => deleteProjectStatusesMutation.mutate()}
                    disabled={deleteProjectStatusesMutation.isPending}
                  >
                    {deleteProjectStatusesMutation.isPending ? "Deleting..." : "Delete All Statuses"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete Vendors Confirmation Dialog */}
            <Dialog open={isDeleteVendorsDialogOpen} onOpenChange={setIsDeleteVendorsDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center text-orange-600">
                    <AlertTriangle className="mr-2 h-5 w-5" />
                    Delete All Vendors
                  </DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete all vendors? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <p className="text-sm text-orange-800">
                      This will permanently delete <strong>{collectionsData?.data?.vendors?.count || 0} vendors</strong> from your database.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsDeleteVendorsDialogOpen(false)}
                    disabled={deleteVendorsMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => deleteVendorsMutation.mutate()}
                    disabled={deleteVendorsMutation.isPending}
                  >
                    {deleteVendorsMutation.isPending ? "Deleting..." : "Delete All Vendors"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* Sessions Management Dialog */}
        <Dialog open={isSessionsDialogOpen} onOpenChange={setIsSessionsDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Users className="mr-2 h-5 w-5" />
                Active Login Sessions
              </DialogTitle>
              <DialogDescription>
                Manage your active login sessions. You can revoke access from devices you no longer use.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {mockSessions.map((session) => (
                <div key={session.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium">{session.device}</h4>
                      {session.isCurrent && (
                        <Badge className="bg-green-500 text-white">Current Session</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{session.location}</p>
                    <p className="text-xs text-gray-500">IP: {session.ip} • Last active: {session.lastActive}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!session.isCurrent && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleRevokeSession(session.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Revoke
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={() => setIsSessionsDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </Tabs>
    </div>
  );
}

// Import/Export Data Section Component
function ImportExportSection() {
  const { toast } = useToast();
  const [exportMode, setExportMode] = useState("user-friendly");
  const [exporting, setExporting] = useState({
    customers: false,
    projects: false,
    leads: false,
    workItems: false,
    database: false
  });
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importMode, setImportMode] = useState("merge"); // "merge" or "restore"
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoreConfirmText, setRestoreConfirmText] = useState("");

  const handleExport = async (entityType) => {
    setExporting(prev => ({ ...prev, [entityType]: true }));
    
    try {
      const token = localStorage.getItem('auth_token');
      const backendUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_BACKEND_URL) || window.location.origin;
      
      // Build the download URL with token in query params
      let downloadUrl;
      if (entityType === 'database') {
        // Full database export (JSON format)
        downloadUrl = `${backendUrl}/api/exports/database?token=${token}`;
      } else if (entityType === 'workItems') {
        // Work items export doesn't use mode parameter (always exports all fields)
        downloadUrl = `${backendUrl}/api/exports/work-items?token=${token}`;
      } else {
        // Other exports use mode parameter
        downloadUrl = `${backendUrl}/api/exports/${entityType}?mode=${exportMode}&token=${token}`;
      }
      
      // Use fetch + blob approach instead of window.open to avoid about:blank issue
      const response = await fetch(downloadUrl);
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }
      
      // Get the blob data
      const blob = await response.blob();
      
      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      const defaultExtension = entityType === 'database' ? 'json' : 'csv';
      let filename = `${entityType}_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.${defaultExtension}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      const successMessage = entityType === 'database' 
        ? 'Full database exported successfully! JSON file downloaded.'
        : `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} exported successfully! File downloaded.`;
      
      toast({
        description: successMessage
      });
      
    } catch (error) {
      toast({
        variant: "destructive",
        description: error.message || `Failed to export ${entityType}`
      });
    } finally {
      setExporting(prev => ({ ...prev, [entityType]: false }));
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast({
          variant: "destructive",
          description: "Please select a JSON file"
        });
        return;
      }
      setSelectedFile(file);
      setImportResults(null);
    }
  };

  const handleImport = async (forceRestore = false) => {
    if (!selectedFile) {
      toast({
        variant: "destructive",
        description: "Please select a file to import"
      });
      return;
    }

    // For restore mode, require confirmation
    if (importMode === "restore" && !forceRestore) {
      setShowRestoreConfirm(true);
      return;
    }

    setImporting(true);
    setImportResults(null);
    setShowRestoreConfirm(false);

    try {
      const token = localStorage.getItem('auth_token');
      const backendUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_BACKEND_URL) || window.location.origin;

      const formData = new FormData();
      formData.append('file', selectedFile);

      // Use different endpoint for full restore vs merge
      const endpoint = importMode === "restore" 
        ? `${backendUrl}/api/imports/database/restore`
        : `${backendUrl}/api/imports/database`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Import failed');
      }

      setImportResults(data);
      
      const message = importMode === "restore"
        ? `Database restored! ${data.summary.total_inserted} records restored across ${data.summary.total_collections} collections.`
        : `Import completed! ${data.summary.total_inserted} inserted, ${data.summary.total_updated} updated, ${data.summary.total_skipped} skipped.`;
      
      toast({ description: message });

      // Clear file selection
      setSelectedFile(null);
      setRestoreConfirmText("");
      // Reset file input
      const fileInput = document.getElementById('import-file-input');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      toast({
        variant: "destructive",
        description: error.message || "Failed to import database"
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Import Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Database className="mr-2 h-5 w-5 text-green-600" />
            Import Database
          </CardTitle>
          <CardDescription>
            Import database from a JSON backup file. Supports both merge and full restore modes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Import Mode Selection */}
          <div className="space-y-2">
            <Label>Import Mode</Label>
            <Select value={importMode} onValueChange={setImportMode}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="merge">
                  Merge (Add new records, update existing)
                </SelectItem>
                <SelectItem value="restore">
                  Full Restore (Replace all data - use for backup recovery)
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {importMode === "merge" 
                ? "Safely merges data without deleting existing records. New records are added, existing records are updated."
                : "⚠️ WARNING: Deletes ALL existing data and replaces with backup file. Use only for disaster recovery."}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="import-file-input">Select JSON Backup File</Label>
            <Input
              id="import-file-input"
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              disabled={importing}
            />
            {selectedFile && (
              <p className="text-sm text-muted-foreground">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>

          <Button
            onClick={() => handleImport(false)}
            disabled={!selectedFile || importing}
            className={`w-full ${importMode === "restore" ? "bg-red-600 hover:bg-red-700" : ""}`}
          >
            {importing ? "Importing..." : importMode === "restore" ? "Restore Database" : "Import Database"}
          </Button>

          {/* Restore Confirmation Dialog */}
          {showRestoreConfirm && (
            <div className="mt-4 p-4 border-2 border-red-500 rounded-lg bg-red-50">
              <div className="flex items-start mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-red-900">⚠️ Full Restore Warning</h4>
                  <p className="text-sm text-red-700 mt-1">
                    This will <strong>DELETE ALL EXISTING DATA</strong> in your database and replace it with the backup file.
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-red-900">Type "RESTORE" to confirm:</Label>
                  <Input
                    value={restoreConfirmText}
                    onChange={(e) => setRestoreConfirmText(e.target.value)}
                    placeholder="RESTORE"
                    className="mt-1 border-red-300"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowRestoreConfirm(false);
                      setRestoreConfirmText("");
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleImport(true)}
                    disabled={restoreConfirmText !== "RESTORE" || importing}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    {importing ? "Restoring..." : "Confirm Full Restore"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Import Results */}
          {importResults && (
            <div className={`mt-4 p-4 border rounded-lg ${importResults.mode === "full_restore" ? "bg-blue-50" : "bg-green-50"}`}>
              <h4 className={`font-semibold mb-2 ${importResults.mode === "full_restore" ? "text-blue-900" : "text-green-900"}`}>
                {importResults.mode === "full_restore" ? "Restore Complete" : "Import Summary"}
              </h4>
              <div className={`space-y-1 text-sm ${importResults.mode === "full_restore" ? "text-blue-800" : "text-green-800"}`}>
                <p>✓ Collections processed: {importResults.summary.total_collections}</p>
                <p>✓ Records inserted: {importResults.summary.total_inserted}</p>
                {importResults.summary.total_updated > 0 && (
                  <p>✓ Records updated: {importResults.summary.total_updated}</p>
                )}
                {importResults.summary.total_skipped > 0 && (
                  <p>✓ Records skipped (identical): {importResults.summary.total_skipped}</p>
                )}
                {importResults.summary.total_deleted > 0 && (
                  <p>✓ Records deleted: {importResults.summary.total_deleted}</p>
                )}
                {importResults.summary.total_errors > 0 && (
                  <p className="text-red-600">✗ Errors: {importResults.summary.total_errors}</p>
                )}
              </div>
              
              {/* Detailed Results */}
              {importResults.details && importResults.details.length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer font-medium text-sm">View detailed results</summary>
                  <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                    {importResults.details.map((detail, idx) => (
                      <div key={idx} className="text-xs p-2 bg-white rounded border">
                        <p className="font-medium">{detail.collection}</p>
                        <p>
                          {detail.inserted > 0 && `Inserted: ${detail.inserted}`}
                          {detail.updated > 0 && `, Updated: ${detail.updated}`}
                          {detail.skipped > 0 && `, Skipped: ${detail.skipped}`}
                          {detail.deleted > 0 && `, Deleted: ${detail.deleted}`}
                        </p>
                        {detail.errors && detail.errors.length > 0 && (
                          <p className="text-red-600">Errors: {detail.errors.length}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          <div className={`p-4 border rounded-lg ${importMode === "restore" ? "bg-red-50 border-red-200" : "bg-yellow-50 border-yellow-200"}`}>
            <div className="flex items-start">
              <AlertTriangle className={`h-5 w-5 mr-2 mt-0.5 ${importMode === "restore" ? "text-red-600" : "text-yellow-600"}`} />
              <div className="text-sm">
                <p className={`font-medium ${importMode === "restore" ? "text-red-900" : "text-yellow-900"}`}>
                  {importMode === "restore" ? "Full Restore Mode" : "Merge Mode"}
                </p>
                <ul className={`mt-2 space-y-1 list-disc list-inside ${importMode === "restore" ? "text-red-700" : "text-yellow-700"}`}>
                  {importMode === "restore" ? (
                    <>
                      <li><strong>All existing data will be deleted</strong></li>
                      <li>Database will be replaced with backup file contents</li>
                      <li>Use for disaster recovery or migrating to new environment</li>
                      <li>All relationships (projects ↔ customers) will be preserved</li>
                    </>
                  ) : (
                    <>
                      <li>Existing data is preserved</li>
                      <li>Records with same ID will be updated</li>
                      <li>New records will be inserted</li>
                      <li>All relationships are maintained</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Import Vendors Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="mr-2 h-5 w-5 text-blue-600" />
            Import Vendors
          </CardTitle>
          <CardDescription>
            Import vendors from a JSON file
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="import-vendors-file-input">Select Vendors JSON File</Label>
            <Input
              id="import-vendors-file-input"
              type="file"
              accept=".json"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  if (!file.name.endsWith('.json')) {
                    toast({
                      variant: "destructive",
                      description: "Please select a JSON file"
                    });
                    return;
                  }
                  setSelectedFile(file);
                }
              }}
              disabled={importing}
            />
            {selectedFile && (
              <p className="text-sm text-muted-foreground">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>

          <Button
            onClick={async () => {
              if (!selectedFile) {
                toast({
                  variant: "destructive",
                  description: "Please select a file to import"
                });
                return;
              }

              setImporting(true);

              try {
                const token = localStorage.getItem('auth_token');
                const backendUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_BACKEND_URL) || window.location.origin;

                const formData = new FormData();
                formData.append('file', selectedFile);

                const response = await fetch(`${backendUrl}/api/imports/vendors`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`
                  },
                  body: formData
                });

                const data = await response.json();

                if (!response.ok) {
                  throw new Error(data.detail || 'Vendor import failed');
                }

                toast({
                  description: `Vendors imported! ${data.inserted || 0} inserted, ${data.updated || 0} updated, ${data.skipped || 0} skipped.`
                });

                // Clear file selection
                setSelectedFile(null);
                const fileInput = document.getElementById('import-vendors-file-input');
                if (fileInput) fileInput.value = '';

              } catch (error) {
                toast({
                  variant: "destructive",
                  description: error.message || "Failed to import vendors"
                });
              } finally {
                setImporting(false);
              }
            }}
            disabled={!selectedFile || importing}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {importing ? "Importing..." : "Import Vendors"}
          </Button>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <Database className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900">Expected JSON Format</p>
                <pre className="mt-2 text-xs text-blue-700 bg-white p-2 rounded border overflow-x-auto">
{`[
  {
    "id": "vendor-id",
    "company_name": "ABC Supply",
    "contact_name": "John Doe",
    "email": "john@abc.com",
    "phone": "555-1234"
  }
]`}
                </pre>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Database className="mr-2 h-5 w-5" />
            Export Data
          </CardTitle>
          <CardDescription>
            Export your CRM data for backup, migration, or analysis
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Full Database Export */}
          <div className="p-4 bg-purple-50 border-2 border-purple-300 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-purple-900">Export Full Database Backup (JSON)</h4>
                <p className="text-sm text-purple-700 mt-1">
                  Complete backup with all data and relationships preserved. Perfect for disaster recovery or migration.
                </p>
                <ul className="text-xs text-purple-600 mt-2 list-disc list-inside">
                  <li>Preserves all IDs and relationships (projects ↔ customers)</li>
                  <li>Can be fully restored with "Full Restore" import mode</li>
                  <li>Includes all collections: customers, projects, leads, invoices, etc.</li>
                </ul>
              </div>
              <Button
                onClick={() => handleExport('database')}
                disabled={exporting.database}
                className="bg-purple-600 hover:bg-purple-700 ml-4"
              >
                {exporting.database ? "Exporting..." : "Export Full Backup"}
              </Button>
            </div>
          </div>

          {/* Export Mode Selection */}
          <div className="space-y-2">
            <Label>Export Mode (for individual exports below)</Label>
            <Select value={exportMode} onValueChange={setExportMode}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user-friendly">
                  User-Friendly (Clean data, readable field names)
                </SelectItem>
                <SelectItem value="all-fields">
                  All Fields (Complete data including internal IDs and timestamps)
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {exportMode === "user-friendly" 
                ? "Exports only relevant fields with clean formatting, suitable for reports and sharing."
                : "Exports all available data including technical fields, suitable for complete backups."}
            </p>
          </div>

          {/* Individual Export Buttons */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-gray-700">Individual Collection Exports (CSV)</h4>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h4 className="font-medium">Customers</h4>
                <p className="text-sm text-muted-foreground">
                  Export all customer records with contact information and history
                </p>
              </div>
              <Button
                onClick={() => handleExport('customers')}
                disabled={exporting.customers}
              >
                {exporting.customers ? "Exporting..." : "Export Customers"}
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h4 className="font-medium">Projects</h4>
                <p className="text-sm text-muted-foreground">
                  Export all project records with status, budget, and timeline data
                </p>
              </div>
              <Button
                onClick={() => handleExport('projects')}
                disabled={exporting.projects}
              >
                {exporting.projects ? "Exporting..." : "Export Projects"}
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h4 className="font-medium">Leads</h4>
                <p className="text-sm text-muted-foreground">
                  Export all lead records with contact details and status information
                </p>
              </div>
              <Button
                onClick={() => handleExport('leads')}
                disabled={exporting.leads}
              >
                {exporting.leads ? "Exporting..." : "Export Leads"}
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h4 className="font-medium">Work Items</h4>
                <p className="text-sm text-muted-foreground">
                  Export all work item records with all fields (name, category, pricing, etc.)
                </p>
              </div>
              <Button
                onClick={() => handleExport('workItems')}
                disabled={exporting.workItems}
              >
                {exporting.workItems ? "Exporting..." : "Export Work Items"}
              </Button>
            </div>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900">Export Information</p>
                <ul className="mt-2 space-y-1 text-blue-700 list-disc list-inside">
                  <li>Files are downloaded as CSV format (compatible with Excel and Google Sheets)</li>
                  <li>Exports include all records currently in the database</li>
                  <li>Large exports may take a few moments to process</li>
                  <li>Data is timestamped in the filename for easy organization</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}